import { useEffect, useRef, useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { Button, EmptyState, Spinner, Icon } from "@cntrl/ui";
import { useCapability } from "../../../app/bandContext.js";
import { useRelease, useUpdateRelease, useAddBudgetLine, useDeleteBudgetLine, releaseUpdateBody } from "../../../app/hooks/useRelease.js";
import { formatDollars, formatMoney } from "../releaseFormat.js";
import styles from "../section.module.scss";
import own from "./BudgetSection.module.scss";

// Budget (shot-16) — Total/Funding inputs (release PUT), the line table with a LIVE ad-spend row
// (kind='ad_spend_sync', server-owned, NOT user-editable), an add-line form (manual lines, per-row POST), and
// Total/Spent/Remaining tiles (server-derived — remaining is never computed client-side).
//
// EVERY figure here is money on the `money:marketing` axis, so the whole section rides the composed doc's
// nullable `budget` BLOCK and the shell drops the tab without the token. This screen was the reason the gate
// mattered: it reads `data.budget` off the composed release rather than the `/budget-lines` routes, which were
// gated on 2026-09-03 while the composed read was not — so every releases:read seat, interns included, read
// release budgets here until 2026-09-09. Read the totals from `budget`, never from `overview`: they used to sit
// on both, and two authorities for one figure is how a gated copy and an ungated one come apart.

export function BudgetSection({ releaseId }: { releaseId: string }) {
  const canWrite = useCapability("releases:write");
  const q = useRelease(releaseId);
  const update = useUpdateRelease(releaseId);
  const addLine = useAddBudgetLine(releaseId);
  const removeLine = useDeleteBudgetLine(releaseId);

  const overview = q.data?.data?.overview;
  const budget = q.data?.data?.budget;
  const version = q.data?.version;

  const [newLabel, setNewLabel] = useState("");
  const [newAmount, setNewAmount] = useState("");
  // Total budget is CONTROLLED and edits the release aggregate in a full PUT. null = show the server value;
  // re-synced whenever the aggregate version advances. The input disables while a write is in flight, so a
  // second blur cannot fire against a consumed If-Match version. (Funding was removed from this surface per
  // Ryan, ART-201 — its stored value is carried forward untouched on every save below.)
  const [totalStr, setTotalStr] = useState<string | null>(null);
  const [editingTotal, setEditingTotal] = useState(false);
  const totalInputRef = useRef<HTMLInputElement>(null);
  const syncedVersion = useRef<number | null>(null);
  useEffect(() => {
    if (version !== undefined && version !== syncedVersion.current) {
      setTotalStr(null);
      syncedVersion.current = version;
    }
  }, [version]);
  // Focus the total field when editing opens — programmatically, since the `autoFocus` prop trips jsx-a11y.
  useEffect(() => {
    if (editingTotal) totalInputRef.current?.focus();
  }, [editingTotal]);

  if (q.isLoading || !overview) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading budget…" />
      </div>
    );
  }

  // The block is absent for a seat without money:marketing. The shell already drops the tab, so this is only
  // reachable by a deep link — but it is a designed state rather than the permanent spinner that a `!budget`
  // loading guard produced, which is the "still loading" lie a missing gate always tells.
  if (!budget) {
    return (
      <EmptyState
        title="The budget isn't part of your access"
        description="Ask an owner or manager if you need this release's spend."
      />
    );
  }

  const totalValue = totalStr ?? centsToDollars(budget.totalBudgetCents);

  function saveTotals() {
    if (version === undefined) return;
    const totalCents = dollarsToCents(totalValue);
    // No-op when the total didn't change vs server (exact-cents compare — a mere focus+blur never rewrites).
    if (totalCents === (budget!.totalBudgetCents ?? null)) return;
    update.mutate(
      {
        version,
        // Funding is no longer edited here; carry its stored value forward so a total edit never clears it.
        body: releaseUpdateBody(overview!, { totalBudgetCents: totalCents, fundingCents: budget!.fundingCents ?? null }),
      },
      { onSuccess: () => { setTotalStr(null); } },
    );
  }

  function commitTotal() {
    saveTotals(); // a no-op when unchanged
    setEditingTotal(false);
  }
  function cancelTotal() {
    setTotalStr(null); // revert to the server value
    setEditingTotal(false);
  }

  function addBudgetLine() {
    const cents = dollarsToCents(newAmount);
    if (!newLabel.trim() || cents === null) return;
    addLine.mutate(
      { label: newLabel.trim(), amountCents: cents },
      {
        onSuccess: () => {
          setNewLabel("");
          setNewAmount("");
        },
      },
    );
  }

  const lines = budget.lines ?? [];

  return (
    <div className={styles.section}>
      <div className={styles.card}>
        {lines.length === 0 ? (
          <EmptyState title="No budget lines yet" description="Add spend lines below; the live ad-spend row syncs from Meta automatically." />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Item</th>
                <th className={styles.numeric}>Amount</th>
                {canWrite && <th aria-label="Remove" />}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={line.lineId ?? `sync-${i}`}>
                  <td className={own.itemName}>
                    {line.label || "Line"}
                    {line.kind === "ad_spend_sync" && (
                      <span className={own.liveTag}>
                        <span className={own.liveDot} aria-hidden="true" /> Live
                      </span>
                    )}
                  </td>
                  <td className={styles.numeric} data-negative={((line.amountCents ?? 0) < 0) || undefined}>
                    {formatMoney(line.amountCents)}
                  </td>
                  {canWrite && (
                    <td className={styles.numeric}>
                      {line.editable && line.lineId ? (
                        <button
                          type="button"
                          className={own.remove}
                          aria-label="Remove line"
                          onClick={() => removeLine.mutate(line.lineId as string)}
                        >
                          <Icon icon={Trash2} size={16} />
                        </button>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canWrite && (
          <div className={own.addRow}>
            <input className={own.input} placeholder="New item name" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            <input className={own.inputNarrow} placeholder="Amount $" inputMode="numeric" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} />
            <Button variant="ghost" size="sm" onClick={addBudgetLine} loading={addLine.isPending}>
              <Icon icon={Plus} size={16} /> Add line
            </Button>
          </div>
        )}
      </div>

      {/* Total sits inline with the derived Spent / Remaining. Total carries an Edit affordance top-right; the
          figure becomes a text field with a crimson CHECK baked onto its right (ART-201) — commits on the
          check, Enter or blur (a no-op when unchanged), Escape cancels. */}
      <div className={own.tiles}>
        <div className={own.tile}>
          <div className={own.tileHead}>
            <span className={own.tileLabel}>Total budget</span>
            {canWrite && !editingTotal && (
              <button type="button" className={own.tileEdit} onClick={() => setEditingTotal(true)}>
                Edit
              </button>
            )}
          </div>
          {canWrite && editingTotal ? (
            <div className={own.totalEdit}>
              <input
                ref={totalInputRef}
                className={own.totalEditInput}
                inputMode="decimal"
                placeholder="0"
                value={totalValue}
                disabled={update.isPending}
                aria-label="Total budget"
                onChange={(e) => setTotalStr(e.target.value)}
                onBlur={commitTotal}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitTotal();
                  else if (e.key === "Escape") cancelTotal();
                }}
              />
              <button
                type="button"
                className={own.totalEditConfirm}
                aria-label="Save total budget"
                disabled={update.isPending}
                // Keep focus on mousedown so the input's onBlur doesn't pre-empt (and unmount before) this click.
                onMouseDown={(e) => e.preventDefault()}
                onClick={commitTotal}
              >
                <Icon icon={Check} size={16} />
              </button>
            </div>
          ) : (
            <span className={own.tileVal}>{formatDollars(budget.totalBudgetCents)}</span>
          )}
        </div>
        <Tile label="Spent" value={formatMoney(budget.spentCents)} tone={budget.spentCents ? "spent" : undefined} />
        <Tile label="Remaining" value={formatMoney(budget.remainingCents)} />
      </div>
    </div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: "spent" }) {
  return (
    <div className={own.tile} data-tone={tone}>
      <span className={own.tileLabel}>{label}</span>
      <span className={own.tileVal}>{value}</span>
    </div>
  );
}

/** Parse a dollars string to integer cents; null on blank/invalid. Accepts "$1,234.56" or "1234". */
function dollarsToCents(s: string): number | null {
  const cleaned = s.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
}

/** Integer cents → an EXACT plain dollars string for an editable input (no symbol/commas; the inverse of
 *  dollarsToCents so a round-trip never mutates the value). "" for null; whole dollars drop the ".00". */
function centsToDollars(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  return frac === 0 ? `${sign}${whole}` : `${sign}${whole}.${String(frac).padStart(2, "0")}`;
}
